import { useCallback, useMemo, useRef, useState } from "react";
import {
  useV2ListUserViewsForAccountQuery,
  useV2UpdateUserViewMutation,
  useV2ResetUserViewMutation,
} from "@generated";
import { DEFAULT_COLUMNS } from "../columns/columnRegistry";
import type { ProjectsViewFilters, UserViewColumn } from "../types";

export interface ProjectsUserViewPatch {
  columns?: UserViewColumn[];
  filters?: ProjectsViewFilters;
  pageSize?: number;
}

export interface ProjectsUserView {
  isLoading: boolean;
  loadFailed: boolean;
  canSave: boolean;
  columns: UserViewColumn[];
  filters: ProjectsViewFilters;
  pageSize: number;
  saveError: boolean;
  clearSaveError: () => void;
  saveView: (patch: ProjectsUserViewPatch) => Promise<void>;
  resetView: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 25;

interface ViewState {
  columns: UserViewColumn[];
  filters: ProjectsViewFilters;
  pageSize: number;
}

export function useProjectsUserView(): ProjectsUserView {
  // The generated user-view endpoints carry no cache tags, so a remount would otherwise read the
  // pre-save cache and PATCH stale data. Always refetch on mount.
  const {
    data,
    isLoading: initialLoading,
    isFetching,
    isError,
    refetch,
  } = useV2ListUserViewsForAccountQuery(
    { filter: { "view-type": "project" } },
    { refetchOnMountOrArgChange: true }
  );
  // Until the first fetch for this mount settles, any cached `data` may be stale: treat as loading.
  const settled = useRef(false);
  if (!isFetching) settled.current = true;
  const isLoading = !!initialLoading || (!!isFetching && !settled.current);
  const [updateUserView] = useV2UpdateUserViewMutation();
  const [resetUserView] = useV2ResetUserViewMutation();

  const record = data?.data?.[0];
  const loadFailed = !isLoading && (isError || !record);
  const canSave = !!record && !isError && !isLoading;

  const server: ViewState = useMemo(() => {
    const attrs = (record?.attributes ?? {}) as Record<string, unknown>;
    const cols = attrs.columns as UserViewColumn[] | undefined;
    return {
      columns: Array.isArray(cols) && cols.length ? cols : DEFAULT_COLUMNS,
      filters: (attrs.filters as ProjectsViewFilters | undefined) ?? {},
      pageSize: (attrs["page-size"] as number | undefined) ?? DEFAULT_PAGE_SIZE,
    };
  }, [record]);

  // Local overrides sit on top of the server state until the next server update.
  const [override, setOverrideState] = useState<Partial<ViewState> | null>(null);
  // Ref mirrors state so same-tick calls accumulate instead of reading a stale closure.
  const overrideRef = useRef<Partial<ViewState> | null>(null);
  const setOverride = useCallback((next: Partial<ViewState> | null) => {
    overrideRef.current = next;
    setOverrideState(next);
  }, []);
  const [saveError, setSaveError] = useState(false);
  const saveSeq = useRef(0);

  const current: ViewState = { ...server, ...(override ?? {}) };

  const saveView = useCallback(
    async (patch: ProjectsUserViewPatch) => {
      const previous = overrideRef.current;
      const next: Partial<ViewState> = { ...(previous ?? {}), ...patch };
      setOverride(next);
      setSaveError(false);
      if (!canSave || !record?.id) return;
      const seq = ++saveSeq.current;
      const merged = { ...server, ...next };
      try {
        await updateUserView({
          id: String(record.id),
          body: {
            data: {
              id: String(record.id),
              type: "user-views",
              attributes: {
                ...record.attributes,
                columns: merged.columns,
                filters: merged.filters,
                "page-size": merged.pageSize,
              },
              relationships: record.relationships,
            },
          },
        }).unwrap();
      } catch {
        if (seq === saveSeq.current) {
          setOverride(previous);
          setSaveError(true);
        }
      }
    },
    [canSave, record, server, setOverride, updateUserView]
  );

  const resetView = useCallback(async () => {
    if (!canSave || !record?.id) {
      setOverride(null);
      return;
    }
    try {
      const response = await resetUserView({ id: String(record.id) }).unwrap();
      const attrs = response?.data?.attributes as Record<string, unknown> | undefined;
      const cols = attrs?.columns as UserViewColumn[] | undefined;
      if (attrs && Array.isArray(cols)) {
        // The list cache is not invalidated, so hold the reset values locally.
        setOverride({
          columns: cols.length ? cols : DEFAULT_COLUMNS,
          filters: (attrs.filters as ProjectsViewFilters | undefined) ?? {},
          pageSize: (attrs["page-size"] as number | undefined) ?? DEFAULT_PAGE_SIZE,
        });
      } else {
        setOverride(null);
        refetch?.();
      }
    } catch {
      setSaveError(true);
    }
  }, [canSave, record, resetUserView, setOverride, refetch]);

  const clearSaveError = useCallback(() => setSaveError(false), []);

  return {
    isLoading,
    loadFailed,
    canSave,
    columns: current.columns,
    filters: current.filters,
    pageSize: current.pageSize,
    saveError,
    clearSaveError,
    saveView,
    resetView,
  };
}

export default useProjectsUserView;
